/**
 * useAuth Hook - Dashboard Provider/Admin Authentication
 *
 * This hook handles passwordless authentication for DASHBOARD USERS (Providers, Admins).
 * Login is via 6-digit email codes. Signup requires email verification.
 *
 * ⚠️ DO NOT USE THIS FOR OAUTH CUSTOMER LOGIN ⚠️
 * For OAuth customer authentication, use: components/oauth/useOAuthCustomer.ts
 */

import { useCallback } from 'react'
import { AxiosError } from 'axios'
import { useAuthStateValue } from '../contexts/AuthContext'
import { post, get } from '../api/request'
import { NextPaths } from '../router/next-paths'
import { InviteData, AcceptInviteData } from '../types/auth'

/** API error response structure */
interface ApiErrorResponse {
  message?: string
  error?: string
  statusCode?: number
}

export const useAuth = () => {
  const [auth, setAuth] = useAuthStateValue()

  const requestLoginCode = useCallback(
    async (email: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        const normalizedEmail = email.trim().toLowerCase()
        await post('/ui/provider/auth/logins', { email: normalizedEmail })

        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: null,
          pendingLoginCode: { email: normalizedEmail },
        }))
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Failed to send login code'

        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          pendingLoginCode: null,
        }))

        throw error
      }
    },
    [setAuth],
  )

  const verifyEmail = useCallback(
    async (code: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')

        const response = await post('/ui/provider/auth/email-verifications', {
          emailVerificationCode: code,
        })
        const { user, accessToken, refreshToken } = response.data

        localStorage.setItem('token', accessToken)
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken)
        }

        setAuth({
          user,
          token: accessToken,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pendingLoginCode: null,
        })

        return response.data
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Email verification failed'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const verifyLoginCode = useCallback(
    async (email: string, code: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        const response = await post('/ui/provider/auth/logins/verifications', { email, code })
        const data = response.data

        if (data.requiresMfa && !data.accessToken) {
          setAuth(prev => ({ ...prev, isLoading: false }))
          return data
        }

        const { accessToken, refreshToken, user } = data

        localStorage.setItem('token', accessToken)
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken)
        }

        setAuth({
          user,
          token: accessToken,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pendingLoginCode: null,
        })

        return data
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Invalid login code'

        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))

        return null
      }
    },
    [setAuth],
  )

  const verifyProviderMfa = useCallback(
    async (email: string, totpCode: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        const response = await post('/ui/provider/auth/two-factor-login-verifications', {
          email,
          totpCode,
        })
        const { accessToken, refreshToken, user } = response.data

        localStorage.setItem('token', accessToken)
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken)
        }

        setAuth({
          user,
          token: accessToken,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pendingLoginCode: null,
        })

        return response.data
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Invalid verification code'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const resendLoginCode = useCallback(
    async (email: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        await post('/ui/provider/auth/logins/resends', { email })
        setAuth(prev => ({ ...prev, isLoading: false, error: null }))
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Failed to resend login code'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const initiateTwoFactorSetup = useCallback(async () => {
    setAuth(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await post('/ui/provider/auth/two-factor-setups')
      setAuth(prev => ({ ...prev, isLoading: false, error: null }))
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>
      const errorMessage = axiosError.response?.data?.message || 'Failed to initialize 2FA setup'
      setAuth(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw error
    }
  }, [setAuth])

  const completeNewUserTwoFactorSetup = useCallback(
    async (_email: string, code: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        const response = await post('/ui/provider/auth/two-factor-verifications', {
          twoFactorToken: code,
        })

        setAuth(prev => ({ ...prev, isLoading: false, error: null }))
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Failed to complete 2FA setup'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const verifyProviderMfaWithBackup = useCallback(
    async (email: string, backupCode: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        const response = await post('/ui/provider/auth/two-factor-login-verifications/backup', {
          email,
          backupCode,
        })
        const { accessToken, refreshToken, user } = response.data

        localStorage.setItem('token', accessToken)
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken)
        }

        setAuth({
          user,
          token: accessToken,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pendingLoginCode: null,
        })

        return response.data
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Invalid backup code'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const disableTwoFactor = useCallback(async (totpCode: string) => {
    try {
      const response = await post('/ui/provider/auth/two-factor-disables', { totpCode })
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>
      const errorMessage =
        axiosError.response?.data?.message || 'Failed to disable two-factor authentication'
      throw new Error(errorMessage)
    }
  }, [])

  const createInvite = useCallback(async (data: InviteData) => {
    try {
      const response = await post('/ui/provider/auth/invites', data)
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>
      const errorMessage = axiosError.response?.data?.message || 'Failed to create invite'
      throw new Error(errorMessage)
    }
  }, [])

  const acceptInvite = useCallback(async (data: AcceptInviteData) => {
    try {
      const response = await post('/ui/provider/auth/accepted-invites', data)
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>
      const errorMessage = axiosError.response?.data?.message || 'Failed to accept invite'
      throw new Error(errorMessage)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await post('/ui/provider/auth/logouts')
    } catch (error) {
      console.error('Logout API call failed:', error)
    }

    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')

    window.location.href = NextPaths.login
  }, [])

  const clearError = useCallback(() => {
    setAuth(prev => ({ ...prev, error: null }))
  }, [setAuth])

  const clearPendingLoginCode = useCallback(() => {
    setAuth(prev => ({
      ...prev,
      pendingLoginCode: null,
      error: null,
    }))
  }, [setAuth])

  const register = useCallback(
    async (data: { email: string; name: string }) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))

      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')

      try {
        const response = await post('/ui/provider/auth/signups', data)
        setAuth(prev => ({ ...prev, isLoading: false }))
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage = axiosError.response?.data?.message || 'Signup failed'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const resendVerificationCode = useCallback(
    async (email: string) => {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }))
      try {
        await post('/ui/provider/auth/email-verifications/resends', { email })
        setAuth(prev => ({ ...prev, isLoading: false, error: null }))
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorMessage =
          axiosError.response?.data?.message || 'Failed to resend verification code'
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [setAuth],
  )

  const refreshUser = useCallback(async () => {
    try {
      const response = await get('/ui/users/me')
      const user = response.data
      setAuth(prev => ({ ...prev, user }))
      return user
    } catch (error) {
      console.error('Failed to refresh user:', error)
      throw error
    }
  }, [setAuth])

  return {
    // State
    user: auth.user,
    token: auth.token,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    pendingLoginCode: auth.pendingLoginCode,

    // Actions
    requestLoginCode,
    verifyLoginCode,
    verifyProviderMfa,
    verifyProviderMfaWithBackup,
    resendLoginCode,
    initiateTwoFactorSetup,
    completeNewUserTwoFactorSetup,
    disableTwoFactor,
    register,
    verifyEmail,
    resendVerificationCode,
    createInvite,
    acceptInvite,
    logout,
    refreshUser,
    clearError,
    clearPendingLoginCode,
  }
}
